import i18n from '@/locales/config';
import { Modal } from 'antd';

export interface FileValidationFailure {
  name?: string;
  error?: string;
  error_code?: string;
  cause_code?: string;
  parse_enqueued?: boolean | null;
  message_key?: string;
  declared_format?: string;
  detected_format?: string;
}

const VALIDATION_ERROR_MESSAGE_KEYS: Record<string, string> = {
  FILE_EMPTY: 'upload.fileEmpty',
  FILE_FORMAT_INVALID: 'upload.fileFormatInvalid',
  FILE_HEADER_MISMATCH: 'upload.fileHeaderMismatch',
  FILE_CORRUPTED: 'upload.fileCorrupted',
  FILE_CONTENT_INVALID: 'upload.fileContentInvalid',
  FILE_ENCRYPTED: 'upload.fileEncrypted',
  FILE_PARSE_EMPTY: 'upload.fileParseEmpty',
  FILE_PARSE_FAILED: 'upload.fileParseFailed',
  FILE_TOO_COMPLEX: 'upload.fileTooComplex',
  FILE_VALIDATION_TIMEOUT: 'upload.fileValidationTimeout',
  FILE_VALIDATOR_BUSY: 'upload.fileValidatorBusy',
  FILE_VALIDATOR_UNAVAILABLE: 'upload.fileValidatorUnavailable',
  FILE_VALIDATION_INTERNAL_ERROR: 'upload.fileValidationInternal',
};

const PARSE_ERROR_FALLBACKS: Record<string, string[]> = {
  FILE_FORMAT_INVALID: ['The file format does not match its content.'],
  FILE_ENCRYPTED: [
    'The file is encrypted or password-protected. Remove password protection and try again.',
  ],
  FILE_PARSE_EMPTY: ['The file contains no parseable content.'],
  FILE_PARSE_FAILED: [
    'The file may be damaged, contain unsupported content, or could not be parsed.',
    'The file may be damaged or contain content that is not currently supported.',
  ],
};

const matchStructuredErrorCode = (line: string) =>
  line.match(
    /^(?:(?:\d{2}:\d{2}:\d{2}\s+)?(?:Page\(\d+~\d+\):\s+)?(?:\[ERROR\]\s*)?)(FILE_[A-Z0-9_]+)\b/,
  );

export const getFileValidationFailures = (response: any) => {
  const failures = response?.failed ?? response?.data?.failed ?? [];
  return Array.isArray(failures) ? (failures as FileValidationFailure[]) : [];
};

export const localizeValidationProgressMsg = (msg: string) => {
  if (!msg) return msg;
  const localizedLines: string[] = [];
  const lines = msg.split('\n');

  lines.forEach((line) => {
    const legacyPrepareFailure = line.match(/^(FILE_[A-Z0-9_]+):\s*(.*)$/);
    if (legacyPrepareFailure) {
      const code = legacyPrepareFailure[1];
      const detail = legacyPrepareFailure[2];
      const messageKey = VALIDATION_ERROR_MESSAGE_KEYS[code];
      if (!messageKey) {
        localizedLines.push(line);
        return;
      }
      if (detail && !PARSE_ERROR_FALLBACKS[code]?.includes(detail)) {
        localizedLines.push(detail);
      }
      localizedLines.push(`[ERROR]${i18n.t(messageKey)}`);
      return;
    }

    const match = matchStructuredErrorCode(line);
    const code = match?.[1];
    const messageKey = code && VALIDATION_ERROR_MESSAGE_KEYS[code];
    if (!code || !messageKey) {
      localizedLines.push(line);
      return;
    }
    const message = i18n.t(messageKey, {
      defaultValue: code,
      declared: 'UNKNOWN',
      detected: 'UNKNOWN',
    });
    localizedLines.push(
      line.replace(new RegExp(`${code}\\b.*$`), String(message)),
    );
  });

  return localizedLines.join('\n');
};

export const getFileValidationFailureMessage = (
  failure: FileValidationFailure,
) => {
  if (failure.error?.match(/\n\[ERROR\]FILE_[A-Z0-9_]+\b/)) {
    return localizeValidationProgressMsg(failure.error);
  }
  if (failure.parse_enqueued === null) {
    return (
      failure.error ||
      String(
        i18n.t('upload.parseEnqueueUncertain', {
          defaultValue:
            'The parsing task status is uncertain. Refresh before retrying.',
        }),
      )
    );
  }
  const errorCode = failure.cause_code || failure.error_code || '';
  if (!errorCode.startsWith('FILE_')) {
    return failure.error || errorCode || 'Invalid file';
  }
  return String(
    i18n.t(
      failure.message_key || VALIDATION_ERROR_MESSAGE_KEYS[errorCode] || '',
      {
        defaultValue: failure.error || errorCode || 'Invalid file',
        declared: failure.declared_format?.toUpperCase() || 'UNKNOWN',
        detected: failure.detected_format?.toUpperCase() || 'UNKNOWN',
      },
    ),
  );
};

export const getFileValidationFailureSummary = (
  failures: FileValidationFailure[],
) => {
  if (failures.length === 0) return '';
  const reasons = new Set(
    failures.map(
      (failure) =>
        failure.cause_code || failure.error_code || failure.error || '',
    ),
  );
  if (failures.length === 1) {
    return getFileValidationFailureMessage(failures[0]);
  }
  if (reasons.size === 1) {
    const errorCode = failures[0].cause_code || failures[0].error_code || '';
    const messageKey = VALIDATION_ERROR_MESSAGE_KEYS[errorCode];
    if (errorCode.startsWith('FILE_') && messageKey) {
      return String(i18n.t(messageKey, { defaultValue: errorCode }));
    }
  }
  return String(
    i18n.t('upload.multipleFailureReasons', {
      defaultValue: 'Multiple failure reasons; review each file for details.',
    }),
  );
};

export const showFileValidationFailures = (
  failures: FileValidationFailure[],
) => {
  if (failures.length === 0) return;

  Modal.error({
    title: i18n.t('message.corruptedFilesRejected', {
      defaultValue: 'Some files were rejected',
    }),
    content: (
      <div>
        {failures.map((failure, index) => (
          <div
            key={`${failure.name || 'file'}-${index}`}
            style={{ whiteSpace: 'pre-wrap' }}
          >
            {failure.name ? `${failure.name}: ` : ''}
            {getFileValidationFailureMessage(failure)}
          </div>
        ))}
      </div>
    ),
  });
};
